import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Circle, Sparkles, Crown, ArrowRight } from "lucide-react";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "@/lib/i18n";
import { stashTickerProvenance } from "@/lib/ticker_provenance";
import { startAnalyze } from "@/lib/startAnalyze";
import { appendQueryParams } from "@/lib/url";
import { stashCheckoutIntent } from "@/lib/checkout_intent";

// English source-of-truth for fallback plan content. The translated strings
// for these labels/items are looked up via t("pricing.feature_groups.<label>")
// and t("pricing.features.<item>"); falls back to the original English when
// no translation exists (so backend-served plans never break).
const FALLBACK_PLANS = [
  {
    key: "free", name: "Free", tagline_key: "free_tagline",
    monthly_price_eur: 0, yearly_price_eur: 0, yearly_monthly_equivalent_eur: 0, yearly_savings_pct: 0,
    popular: false, premium_tier: false, cta_key: "free_cta",
    feature_groups: [
      { label: "Core", items: [
        "10 analyses in your first 30 days",
        "5 analyses every rolling 30 days after that",
        "AI Opportunity Score",
        "Fair Value & upside/downside",
        "Risk level",
        "Basic AI summary",
        "Up to 10 watchlist stocks",
      ] },
    ],
  },
  {
    key: "pro", name: "Pro", tagline_key: "pro_tagline",
    monthly_price_eur: 19, yearly_price_eur: 190, yearly_monthly_equivalent_eur: 15.83, yearly_savings_pct: 17,
    popular: true, premium_tier: false, cta_key: "pro_cta",
    feature_groups: [
      { label: "Complete stock research", items: [
        "Unlimited stock analyses",
        "Full AI Insights",
        "Bull & Bear Case",
        "Detailed risk analysis",
        "Why This Score",
        "Sector comparison",
        "Unlimited watchlist",
        "Notes, labels & categories",
        "KPI explanations & tooltips",
      ] },
      { label: "Coming next", items: [
        "Email alerts for price and fair-value changes",
      ] },
    ],
  },
  {
    key: "premium", name: "Premium", tagline_key: "premium_tagline",
    monthly_price_eur: 29, yearly_price_eur: 290, yearly_monthly_equivalent_eur: 24.17, yearly_savings_pct: 17,
    popular: false, premium_tier: true, cta_key: "premium_cta",
    feature_groups: [
      // Phase 8.PH.FINAL — Pricing now reflects only features that are
      // actually implemented and live. "Advanced portfolio analytics"
      // has been removed until it ships; "Premium PDF reports" and
      // "CSV export" are now first-class features (not "coming next").
      { label: "Everything in Pro, plus", items: [
        "Portfolio tracking",
        "Buy-price & position performance",
        "Realized and unrealized P/L",
        "Closed positions",
        "Portfolio Growth dashboard",
        "Portfolio Fair Value",
        "Weighted Opportunity Score & risk",
        "Portfolio AI Review",
        "S&P 500 benchmark",
        "Portfolio refresh",
        "Premium PDF reports",
        "CSV export",
      ] },
    ],
  },
];

export default function Pricing() {
  const [plans, setPlans] = useState(FALLBACK_PLANS);
  const [interval, setInterval] = useState("yearly"); // "monthly" | "yearly"
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, locale } = useTranslation();

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/billing/plans");
        if (res.data.plans) setPlans(res.data.plans);
      } catch { /* fallback */ }
    })();
  }, []);

  const onChoose = async (key) => {
    if (key === "free") {
      if (user) {
        startAnalyze({ navigate, ticker: "AAPL", source: "pricing:free_cta" });
      } else {
        navigate("/signup");
      }
      return;
    }
    if (!user) {
      // Phase 7.UI.15k — stash the intent so we can continue the
      // checkout automatically after signup/login. sessionStorage
      // survives the redirect through /signup and /login.
      stashCheckoutIntent({
        plan: key,
        interval,
        locale: locale || "en",
        source: "pricing",
        return_path: "/pricing",
      });
      navigate("/signup?next=/billing/continue-checkout");
      return;
    }
    // Phase 7.UI.15 — try the CopeCart checkout URL endpoint first. When
    // COPECART_CHECKOUT_ENABLED=false the endpoint returns 503 and we
    // fall back to the legacy /billing page (current safe behavior).
    try {
      // Phase 7.UI.15j — pass the current Stockaily UI locale so the
      // CopeCart checkout page renders in the same language.
      const res = await api.get(
        `/billing/copecart/checkout-url?plan=${key}&interval=${interval}&locale=${encodeURIComponent(locale || "en")}`,
      );
      if (res?.data?.checkout_url) {
        // Defensive double-stamp: if the backend somehow omitted locale
        // (older deploy, future query collision) we still guarantee the
        // final URL carries the user's UI language.
        window.location.href = appendQueryParams(res.data.checkout_url, { locale: locale || "en" });
        return;
      }
    } catch (e) {
      // 503 => checkout disabled => silently fall back to /billing
      // 400 => unsupported combination => fall back to /billing
      // 401 => somehow signed-out => navigate to login
      const status = e?.response?.status;
      if (status === 401) {
        navigate(`/login?next=/pricing`);
        return;
      }
    }
    navigate(`/billing?plan=${key}&interval=${interval}`);
  };

  const proPlan = useMemo(() => plans.find((p) => p.key === "pro"), [plans]);
  const savingsPct = proPlan?.yearly_savings_pct || 17;

  // Translation helpers: try the provided dictionary lookup, fall back to the
  // raw input so backend-served English strings always render correctly.
  const tFeatureGroup = (label) => {
    const translated = t(`pricing.feature_groups.${label}`);
    return translated === `pricing.feature_groups.${label}` ? label : translated;
  };
  const tFeatureItem = (item) => {
    const translated = t(`pricing.features.${item}`);
    return translated === `pricing.features.${item}` ? item : translated;
  };
  // Always derive tagline/CTA from the stable plan key so backend-served
  // English strings get localized too. Falls back to whatever the API
  // sent if no translation key matches.
  const tPlanCta = (plan) => {
    const key = `pricing.plans.${plan.key}_cta`;
    const v = t(key);
    return v === key ? (plan.cta || t("common.upgrade_pro")) : v;
  };
  const tPlanTagline = (plan) => {
    const key = `pricing.plans.${plan.key}_tagline`;
    const v = t(key);
    return v === key ? (plan.tagline || "") : v;
  };

  return (
    <div className="min-h-screen flex flex-col">
      <NavBar showSearch={false} />
      <main className="flex-1 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16" data-testid="pricing-page">
        {/* Phase 7.UI.15 — calm banner when checkout was cancelled at CopeCart */}
        {typeof window !== "undefined" && new URLSearchParams(window.location.search).get("checkout") === "cancelled" ? (
          <div
            className="mb-6 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-700 dark:text-amber-200"
            data-testid="pricing-checkout-cancelled-banner"
          >
            Checkout was cancelled. Your current plan has not changed.
          </div>
        ) : null}
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-card/40 px-3 py-1 text-[11px] uppercase tracking-wider text-muted-foreground">
            <Sparkles className="w-3 h-3 text-[#3182CE]" /> {t("pricing.pill")}
          </div>
          <h1 className="mt-4 font-display text-3xl sm:text-5xl tracking-tight">
            {t("pricing.title_main")}{" "}
            <span className="text-muted-foreground">{t("pricing.title_sub")}</span>
          </h1>
          <p className="mt-3 text-muted-foreground">
            {t("pricing.subtitle")}
          </p>
        </div>

        {/* Interval toggle */}
        <div className="mt-8 flex items-center justify-center" data-testid="pricing-interval-toggle">
          <div className="inline-flex items-center rounded-full border border-border/70 bg-card/60 p-1 shadow-[var(--app-shadow-elev-1)]">
            <IntervalButton active={interval === "monthly"} onClick={() => setInterval("monthly")} testId="pricing-toggle-monthly">
              {t("pricing.monthly")}
            </IntervalButton>
            <IntervalButton active={interval === "yearly"} onClick={() => setInterval("yearly")} testId="pricing-toggle-yearly">
              {t("pricing.yearly")}
              <span
                className="ml-2 inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider"
                style={{
                  color: "#3FAE89",
                  background: "rgba(63,174,137,0.14)",
                  border: "1px solid rgba(63,174,137,0.34)",
                }}
              >
                {t("common.save_pct", { pct: savingsPct })}
              </span>
            </IntervalButton>
          </div>
        </div>

        {/* Plan cards */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="pricing-cards">
          {plans.map((p) => (
            <PlanCard
              key={p.key}
              plan={p}
              interval={interval}
              currentPlan={user ? (user.plan || "free").toLowerCase() : null}
              onChoose={onChoose}
              t={t}
              tFeatureGroup={tFeatureGroup}
              tFeatureItem={tFeatureItem}
              tPlanCta={tPlanCta}
              tPlanTagline={tPlanTagline}
            />
          ))}
        </div>

        {/* Compare table */}
        <ComparisonTable t={t} />

        {/* FAQ-style trust block */}
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <TrustItem title={t("pricing.trust_cancel_title")} body={t("pricing.trust_cancel_body")} />
          <TrustItem title={t("pricing.trust_eu_title")} body={t("pricing.trust_eu_body")} />
          <TrustItem title={t("pricing.trust_nospam_title")} body={t("pricing.trust_nospam_body")} />
        </div>
      </main>
      <Footer />
    </div>
  );
}

function IntervalButton({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`inline-flex items-center gap-1 px-4 py-1.5 rounded-full text-sm font-medium transition-all ${active ? "bg-card text-foreground shadow-[var(--app-shadow-elev-1)]" : "text-muted-foreground hover:text-foreground"}`}
    >
      {children}
    </button>
  );
}

function PlanCard({ plan, interval, currentPlan, onChoose, t, tFeatureGroup, tFeatureItem, tPlanCta, tPlanTagline }) {
  const isYearly = interval === "yearly";
  const monthly = plan.monthly_price_eur || 0;
  const yearly = plan.yearly_price_eur || 0;
  const yearlyMonthly = plan.yearly_monthly_equivalent_eur || (yearly / 12);
  const showPrice = plan.key === "free" ? 0 : (isYearly ? yearlyMonthly : monthly);
  const savings = (isYearly && monthly > 0) ? Math.round((monthly * 12) - yearly) : 0;
  // Phase 7.UI.15m — defensive guard: a logged-out visitor (currentPlan
  // === null) must NEVER see the Free card as "Current plan", and we
  // must never disable the Free CTA for them. Without this guard a
  // future regression that passes `currentPlan: "free"` for everyone
  // would silently re-introduce the bug.
  const isCurrent = !!currentPlan && currentPlan === plan.key;
  const isPopular = !!plan.popular;
  const isPremium = !!plan.premium_tier;

  const accent = isPremium
    ? { fg: "#A47864", border: "rgba(164,120,100,0.45)", glow: "rgba(164,120,100,0.32)" }
    : isPopular
      ? { fg: "#3182CE", border: "rgba(49,130,206,0.55)", glow: "rgba(49,130,206,0.40)" }
      : { fg: "#7D8592", border: "rgba(125,133,146,0.32)", glow: "rgba(125,133,146,0.18)" };

  return (
    <div
      data-testid="pricing-plan-card"
      data-plan={plan.key}
      className={`relative rounded-2xl border p-6 bg-card/60 flex flex-col transition-all ${isPopular ? "shadow-[0_0_60px_-20px_rgba(49,130,206,0.45)]" : isPremium ? "shadow-[0_0_60px_-22px_rgba(164,120,100,0.4)]" : "shadow-[var(--app-shadow-elev-1)]"}`}
      style={{ borderColor: accent.border }}
    >
      <div className="absolute inset-0 pointer-events-none rounded-2xl overflow-hidden">
        <div
          className="absolute inset-0"
          style={{ background: `radial-gradient(720px 220px at 0% 0%, ${accent.glow}, transparent 70%)` }}
        />
      </div>
      {isPopular && (
        <div
          className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full border bg-card px-2.5 py-0.5 text-[11px] font-medium"
          style={{ borderColor: accent.border, color: accent.fg }}
        >
          <Sparkles className="w-3 h-3" /> {t("pricing.most_popular")}
        </div>
      )}
      {isPremium && !isPopular && (
        <div
          className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full border bg-card px-2.5 py-0.5 text-[11px] font-medium"
          style={{ borderColor: accent.border, color: accent.fg }}
        >
          <Crown className="w-3 h-3" /> {t("pricing.premium_tier")}
        </div>
      )}

      <div className="relative">
        <div className="flex items-baseline justify-between gap-2">
          <div className="font-display text-xl">{plan.name}</div>
          {isCurrent && (
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground border border-border/70 rounded-full px-1.5 py-0.5">
              {t("common.current")}
            </span>
          )}
        </div>
        <p className="mt-1 text-xs text-muted-foreground min-h-[18px]">{tPlanTagline(plan)}</p>

        <div className="mt-5 flex items-baseline gap-1.5">
          <span className="font-display text-4xl tabular leading-none" style={{ color: accent.fg }}>
            {plan.key === "free"
              ? "\u20AC0"
              : `\u20AC${(showPrice).toFixed(showPrice < 10 || !Number.isInteger(showPrice) ? 2 : 0)}`}
          </span>
          <span className="text-sm text-muted-foreground tabular">{t("common.per_month_short")}</span>
        </div>

        {plan.key !== "free" ? (
          isYearly ? (
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px]">
              <span className="text-muted-foreground tabular">{t("common.billed_yearly", { amount: yearly })}</span>
              {savings > 0 && (
                <span
                  className="inline-flex items-center rounded-full px-1.5 py-0.5 font-semibold tabular"
                  style={{
                    color: "#3FAE89",
                    background: "rgba(63,174,137,0.14)",
                    border: "1px solid rgba(63,174,137,0.34)",
                  }}
                  data-testid={`pricing-card-${plan.key}-savings`}
                >
                  {t("common.save_eur_yr", { amount: savings })}
                </span>
              )}
            </div>
          ) : (
            <div className="mt-1 text-[11px] text-muted-foreground tabular">
              {t("pricing.switch_to_yearly", { amount: Math.round(monthly * 12 - yearly) })}
            </div>
          )
        ) : (
          <div className="mt-1 text-[11px] text-muted-foreground">{t("pricing.no_card")}</div>
        )}

        {/* Feature groups */}
        <div className="mt-5 space-y-4 flex-1">
          {plan.feature_groups?.map((g) => {
            // Phase 8.PRICING.copy — "Coming next" / "Coming soon" groups
            // render with visually subordinate styling (muted text + hollow
            // circle bullet) so the user immediately sees that these are
            // upcoming, not active. Layout and typography scale are
            // preserved to keep card heights and mobile stacking intact.
            const upcoming = /^coming/i.test(g.label || "");
            return (
              <div key={g.label}>
                <div className={"text-[10px] uppercase tracking-wider " + (upcoming ? "text-muted-foreground/60" : "text-muted-foreground")}>
                  {tFeatureGroup(g.label)}
                </div>
                <ul className="mt-1.5 space-y-1.5 text-sm">
                  {g.items.map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      {upcoming ? (
                        <Circle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-muted-foreground/50" strokeWidth={1.5} />
                      ) : (
                        <Check className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: accent.fg }} />
                      )}
                      <span className={"leading-snug " + (upcoming ? "text-muted-foreground/80" : "text-foreground/90")}>
                        {tFeatureItem(item)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <Button
          className="mt-6 w-full"
          variant={(isPopular || isPremium) ? "default" : "outline"}
          onClick={() => onChoose(plan.key)}
          data-testid={`pricing-cta-${plan.key}`}
          disabled={isCurrent}
          style={isPremium && !isCurrent ? {
            // Phase 7.UI.15m — Premium CTA must look at least as
            // strong as Pro. We keep the same high-contrast primary
            // button base (white-on-dark / dark-on-light depending
            // on theme) and layer a stronger warm-tone halo so it
            // still reads "premium" without ever feeling secondary.
            boxShadow:
              "0 0 0 1.5px rgba(164,120,100,0.75), 0 0 0 4px rgba(164,120,100,0.15), 0 8px 28px -10px rgba(164,120,100,0.55)",
          } : undefined}
        >
          {isCurrent ? t("common.current_plan") : (
            <>
              {tPlanCta(plan)} <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

function makeCompareRows(t) {
  return [
    { group: t("pricing.compare.group_analyses"), rows: [
      { label: t("pricing.compare.row_analyses"), values: { free: t("pricing.compare.val_free_analyses"), pro: t("pricing.compare.val_unlimited"), premium: t("pricing.compare.val_unlimited") } },
      { label: t("pricing.compare.row_ai_score"), values: { free: true, pro: true, premium: true } },
      { label: t("pricing.compare.row_fair_value"), values: { free: true, pro: true, premium: true } },
      { label: t("pricing.compare.row_ai"), values: { free: t("pricing.compare.val_ai_basic"), pro: t("pricing.compare.val_ai_full"), premium: t("pricing.compare.val_ai_full") } },
      { label: t("pricing.compare.row_ai_risk"), values: { free: t("pricing.compare.val_risk_basic"), pro: true, premium: true } },
      { label: t("pricing.compare.row_bull_bear"), values: { free: false, pro: true, premium: true } },
      { label: t("pricing.compare.row_why_score"), values: { free: t("pricing.compare.val_limited"), pro: true, premium: true } },
      { label: t("pricing.compare.row_sector"), values: { free: false, pro: true, premium: true } },
      { label: t("pricing.compare.row_kpi"), values: { free: false, pro: true, premium: true } },
    ]},
    { group: t("pricing.compare.group_watchlist"), rows: [
      { label: t("pricing.compare.row_wl_count"), values: { free: t("pricing.compare.val_up_to_10"), pro: t("pricing.compare.val_unlimited"), premium: t("pricing.compare.val_unlimited") } },
      { label: t("pricing.compare.row_notes"), values: { free: false, pro: true, premium: true } },
      { label: t("pricing.compare.row_labels"), values: { free: false, pro: true, premium: true } },
      { label: t("pricing.compare.row_alerts"), values: { free: false, pro: t("pricing.compare.val_coming_soon"), premium: t("pricing.compare.val_coming_soon") } },
    ]},
    { group: t("pricing.compare.group_portfolio"), rows: [
      { label: t("pricing.compare.row_portfolio"), values: { free: false, pro: false, premium: true } },
      { label: t("pricing.compare.row_buy_price"), values: { free: false, pro: false, premium: true } },
      { label: t("pricing.compare.row_position_performance"), values: { free: false, pro: false, premium: true } },
      { label: t("pricing.compare.row_unrealized_pl"), values: { free: false, pro: false, premium: true } },
      { label: t("pricing.compare.row_realized_pl"), values: { free: false, pro: false, premium: true } },
      { label: t("pricing.compare.row_closed_positions"), values: { free: false, pro: false, premium: true } },
      { label: t("pricing.compare.row_portfolio_fair_value"), values: { free: false, pro: false, premium: true } },
      { label: t("pricing.compare.row_weighted_opp"), values: { free: false, pro: false, premium: true } },
      { label: t("pricing.compare.row_portfolio_risk"), values: { free: false, pro: false, premium: true } },
      { label: t("pricing.compare.row_portfolio_ai_review"), values: { free: false, pro: false, premium: true } },
      { label: t("pricing.compare.row_dashboard"), values: { free: false, pro: false, premium: true } },
      // Phase 8.E.4 shipped the S&P 500 benchmark into the Premium
      // Portfolio Growth chart, so this row is now marked Included.
      { label: t("pricing.compare.row_sp500"), values: { free: false, pro: false, premium: true } },
      { label: t("pricing.compare.row_portfolio_refresh"), values: { free: false, pro: false, premium: true } },
      // Phase 8.PH.FINAL — PDF and CSV are now shipped Premium features.
      { label: t("pricing.compare.row_pdf_export"), values: { free: false, pro: false, premium: true } },
      { label: t("pricing.compare.row_export"), values: { free: false, pro: false, premium: true } },
      // "Advanced portfolio analytics" is removed from the visible
      // comparison table until it actually ships (volatility, drawdown,
      // correlations, factor exposure, risk contribution, scenario
      // analysis). It remains an internal roadmap item.
    ]},
  ];
}

function ComparisonTable({ t }) {
  const rows = useMemo(() => makeCompareRows(t), [t]);
  return (
    <div
      className="mt-12 rounded-2xl border border-border/70 bg-card/40 overflow-hidden"
      data-testid="pricing-comparison-table"
    >
      {/* Desktop header (hidden on mobile — each cell carries its own label there) */}
      <div className="hidden md:grid grid-cols-12 px-4 sm:px-6 py-4 text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border/70">
        <div className="col-span-6">{t("pricing.compare_features")}</div>
        <div className="col-span-2 text-center">{t("common.free_label")}</div>
        <div className="col-span-2 text-center" style={{ color: "#3182CE" }}>{t("common.pro_label")}</div>
        <div className="col-span-2 text-center" style={{ color: "#A47864" }}>{t("common.premium_label")}</div>
      </div>
      {/* Mobile header */}
      <div className="md:hidden px-4 py-3 text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border/70">
        {t("pricing.compare_features")}
      </div>
      {rows.map((g) => (
        <div key={g.group}>
          <div className="px-4 sm:px-6 py-2 text-[11px] uppercase tracking-wider text-muted-foreground bg-secondary/30">
            {g.group}
          </div>
          {g.rows.map((row) => (
            <React.Fragment key={row.label}>
              {/* Desktop row */}
              <div className="hidden md:grid grid-cols-12 px-4 sm:px-6 py-2.5 text-sm border-t border-border/50 first:border-t-0">
                <div className="col-span-6 text-foreground/90">{row.label}</div>
                <CompareCell value={row.values.free} />
                <CompareCell value={row.values.pro} accent="#3182CE" />
                <CompareCell value={row.values.premium} accent="#A47864" />
              </div>
              {/* Mobile row — stacked label + 3 mini cells */}
              <div className="md:hidden px-4 py-3 border-t border-border/50 first:border-t-0">
                <div className="text-sm font-medium text-foreground/90 leading-snug">{row.label}</div>
                <div className="mt-1.5 grid grid-cols-3 gap-1.5">
                  <MobileCompareCell label={t("common.free_label")} value={row.values.free} accentLabel="#7D8592" />
                  <MobileCompareCell label={t("common.pro_label")} value={row.values.pro} accent="#3182CE" accentLabel="#3182CE" />
                  <MobileCompareCell label={t("common.premium_label")} value={row.values.premium} accent="#A47864" accentLabel="#A47864" />
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
      ))}
    </div>
  );
}

function CompareCell({ value, accent }) {
  let inner;
  if (value === true) inner = <Check className="w-4 h-4 mx-auto" style={{ color: accent || "#3FAE89" }} />;
  else if (value === false) inner = <span className="text-muted-foreground/50">—</span>;
  else inner = <span className="text-xs tabular" style={{ color: accent || "var(--foreground)" }}>{value}</span>;
  return <div className="col-span-2 text-center">{inner}</div>;
}

function MobileCompareCell({ label, value, accent, accentLabel }) {
  let inner;
  if (value === true) inner = <Check className="w-3.5 h-3.5" style={{ color: accent || "#3FAE89" }} />;
  else if (value === false) inner = <span className="text-muted-foreground/50">—</span>;
  else inner = <span className="text-[11px] tabular leading-tight" style={{ color: accent || "var(--foreground)" }}>{value}</span>;
  return (
    <div className="rounded-md border border-border/50 bg-card/30 px-2 py-1.5 flex flex-col gap-1 min-h-[44px]">
      <span className="text-[9px] uppercase tracking-wider" style={{ color: accentLabel || "var(--muted-foreground)" }}>
        {label}
      </span>
      <span className="inline-flex items-center">{inner}</span>
    </div>
  );
}

function TrustItem({ title, body }) {
  return (
    <div className="rounded-xl border border-border/70 bg-card/40 p-4">
      <div className="text-sm font-medium">{title}</div>
      <div className="mt-1 text-xs text-muted-foreground leading-relaxed">{body}</div>
    </div>
  );
}
